import React, { useState, useEffect } from 'react';
import { callApi } from '../api';

// Permission Keys
const PERMISSIONS = [
    { key: 'haberler', label: 'Haber Yönetimi' },
    { key: 'etkinlikler', label: 'Etkinlik Yönetimi' },
    { key: 'basvurular', label: 'Başvuru Yönetimi' },
    { key: 'tarifler', label: 'Tarif Yönetimi' },
    { key: 'galeri', label: 'Galeri Yönetimi' },
    { key: 'uyeler', label: 'Üye Yönetimi' },
    { key: 'site-yonetim', label: 'Site Yönetimi' },
    { key: 'qr-okuyucu', label: 'QR Yoklama' },
    { key: 'analiz', label: 'Raporlar/Analiz' },
    { key: 'ayarlar', label: 'Kulüp / Genel Ayarlar' },
    { key: 'kullanicilar', label: 'Kullanıcı Yönetimi' },
    { key: 'denetim', label: 'Sistem Denetimi (Loglar)' }
];

function KullaniciYonetim() {
    const [users, setUsers] = useState([]);
    const [newUser, setNewUser] = useState({
        username: '',
        permissions: [],
        role: 'editor' // Still kept as label 
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [tempPassword, setTempPassword] = useState(null);
    const [showTempPassword, setShowTempPassword] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const data = await callApi('/auth/users', 'GET');
            setUsers(data.users || []);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setTempPassword(null);
        setShowTempPassword(false);
        try {
            const res = await callApi('/auth/users', 'POST', newUser);
            setSuccess('Kullanıcı oluşturuldu.');
            setTempPassword(res.tempPassword);
            setNewUser({ username: '', permissions: [], role: 'editor' });
            fetchUsers();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (username) => {
        if (!window.confirm(`${username} kullanıcısını silmek istediğinize emin misiniz?`)) return;
        try {
            await callApi('/auth/users', 'DELETE', { username });
            fetchUsers();
            setSuccess(`${username} silindi.`);
        } catch (err) {
            setError(err.message);
        }
    };

    const handlePasswordReset = async (username, newPassword) => {
        setError(null);
        setSuccess(null);
        try {
            await callApi('/auth/users', 'PUT', { username, newPassword });
            setSuccess(`${username} şifresi güncellendi.`);
        } catch (err) {
            setError(err.message);
        }
    };

    const togglePermission = (key) => {
        setNewUser(prev => {
            const perms = prev.permissions.includes(key)
                ? prev.permissions.filter(p => p !== key)
                : [...prev.permissions, key];
            return { ...prev, permissions: perms };
        });
    };

    return (
        <div className="yonetim-container">
            <h2>Kullanıcı Yönetimi</h2>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {tempPassword && (
                <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '15px', borderRadius: '5px', marginBottom: '20px', border: '1px solid #c3e6cb' }}>
                    <strong>✅ Kullanıcı Başarıyla Oluşturuldu!</strong><br />
                    Geçici Şifre: <code 
                        style={{ fontSize: '1.2rem', background: '#fff', padding: '2px 6px', borderRadius: '4px', cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setShowTempPassword(!showTempPassword)}
                        title="Tıklayarak gizle/göster"
                    >
                        {showTempPassword ? tempPassword : '•••••••• (Göster)'}
                    </code><br />
                    <small>Bu şifreyi kullanıcıya iletin. İlk girişte değiştirmeleri istenecektir.</small>
                </div>
            )}

            <div className="card">
                <h3>Yeni Kullanıcı Ekle</h3>
                <form onSubmit={handleCreate}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <div className="form-group">
                            <label>Kullanıcı Adı</label>
                            <input
                                value={newUser.username}
                                onChange={e => setNewUser({ ...newUser, username: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group" style={{ flex: '1 1 100%' }}>
                            <label>Yetkiler (İzin Listesi)</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '5px' }}>
                                {PERMISSIONS.map(perm => (
                                    <label key={perm.key} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#eee', padding: '5px 10px', borderRadius: '15px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={newUser.permissions.includes(perm.key)}
                                            onChange={() => togglePermission(perm.key)}
                                        />
                                        {perm.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <button type="submit" className="save-btn" style={{ marginTop: '10px' }}>Ekle ve Yetkilendir</button>
                    <p style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '5px' }}>* Şifre otomatik oluşturulacaktır.</p>
                </form>
            </div>

            <div className="card" style={{ marginTop: '20px' }}>
                <h3>Mevcut Kullanıcılar</h3>
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Kullanıcı Adı</th>
                            <th>İzinler</th>
                            <th>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.username}>
                                <td data-label="Kullanıcı Adı">{u.username}</td>
                                <td data-label="İzinler">
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                        {u.role === 'superadmin' ? (
                                            <span className="badge superadmin">Tam Yetki</span>
                                        ) : (
                                            (u.permissions || []).map(p => (
                                                <span key={p} style={{ fontSize: '0.75rem', background: '#e0e0e0', padding: '2px 6px', borderRadius: '4px' }}>{p}</span>
                                            ))
                                        )}
                                        {(!u.permissions || u.permissions.length === 0) && u.role !== 'superadmin' && <span style={{ opacity: 0.5 }}>-</span>}
                                    </div>
                                </td>
                                <td data-label="İşlemler">
                                    {u.role !== 'superadmin' && (
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            <button
                                                onClick={() => {
                                                    const newPass = prompt(`${u.username} için yeni şifreyi girin:`);
                                                    if (newPass) {
                                                        handlePasswordReset(u.username, newPass);
                                                    }
                                                }}
                                                className="secondary-button"
                                                style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                                            >
                                                🔑 Şifre
                                            </button>
                                            <button onClick={() => handleDelete(u.username)} className="delete-btn">Sil</button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default KullaniciYonetim;
